import { App } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../../store/store';
import { CopyBox, CopyContainer, isAImgBox, isAVariableBox } from '../../utils/boxes';
import { getUrlParamsObject, searchUrlChangedEvent } from '../../utils/browser';
import { defaultSlider } from '../../constants/constants';
import { debounce } from 'lodash';
import { useBoxContainerActions } from '../../hooks';
const boxSelector = ({
  moveBoxInfinite,
  selectedBoxId,
  addCopy,
  boxes,
  masks,
  boxIsMovable,
  removeBox,
  setSlider,
  slider,
  setCurrentDocument,
  eventManagerEnabled,
  setEventManagerEnabled,
  currentProposal,
  containers,
  boxVariableSelectedId,
  fromPdf,
  selectedContainer,
  selectedContainerIds,
  currentDocument,
  configuration,
  columns,
  rowContainers
}) => {
  let selectedBox = selectedBoxId && [...boxes, ...masks.map((m) => m.boxes).flat()].find((box) => box?.id === selectedBoxId)
  const container = containers.find(container => container.columnsIds.includes(selectedBox?.columnId))
  if (!container?.editable && (isAImgBox(selectedBox?.type) || isAVariableBox(selectedBox?.type)) && container?.hasOwnProperty('editable') && currentProposal.id !== null) {
    selectedBox = {}
  }
  return {
    moveBoxInfinite,
    addCopy,
    selectedBox,
    boxIsMovable,
    removeBox,
    setSlider,
    slider,
    setCurrentDocument,
    eventManagerEnabled,
    setEventManagerEnabled,
    boxVariableSelectedId,
    fromPdf,
    selectedContainer,
    selectedContainerIds,
    currentDocument,
    configuration,
    boxes,
    columns,
    rowContainers
  }
};

const EventManager = ({ children }) => {
  const { modal } = App.useApp();
  const {
    moveBoxInfinite,
    addCopy,
    selectedBox,
    boxIsMovable,
    removeBox,
    setSlider,
    slider,
    setCurrentDocument,
    eventManagerEnabled,
    setEventManagerEnabled,
    boxVariableSelectedId,
    fromPdf,
    selectedContainer,
    selectedContainerIds,
    currentDocument,
    configuration,
    boxes,
    columns,
    rowContainers
  } = useStore(boxSelector);

  // Même suppression de container que la corbeille, dont la boîte de dialogue
  // propose de retirer toute la section quand le container en vient.
  const { deleteSelection: deleteSelectedContainers } = useBoxContainerActions(null, {
    containerOnly: true,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const checkModalState = () => {
      const modalWraps = document.querySelectorAll('.ant-modal-wrap');
      const visibleModals = Array.from(modalWraps).filter(wrap => {
        const style = window.getComputedStyle(wrap);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });

      const newIsModalOpen = visibleModals.length > 0;
      setIsModalOpen(newIsModalOpen);
      setEventManagerEnabled(!newIsModalOpen);
    };

    const observer = new MutationObserver(checkModalState);

    checkModalState();

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'display', 'visibility']
    });

    return () => {
      observer.disconnect();
    };
  }, [setEventManagerEnabled]);

  const handleCtrlCopy = useCallback(
    (event) => {
      if (
        selectedBox &&
        (event.ctrlKey || event.metaKey) &&
        (event.key === 'c' || event.key === 'C') &&
        eventManagerEnabled &&
        !isModalOpen
      ) {
        CopyBox(selectedBox, addCopy);
      }
    },
    [selectedBox, eventManagerEnabled, addCopy, isModalOpen]
  );

  const handleCtrlCopyContainer = useCallback(
    (event) => {
      if (
        selectedContainer &&
        (event.ctrlKey || event.metaKey) &&
        (event.key === 'c' || event.key === 'C') &&
        eventManagerEnabled &&
        !isModalOpen
      ) {
        CopyContainer({ addCopy, selectedContainer, boxes, columns, rowContainers });
      }
    },
    [selectedContainer, eventManagerEnabled, addCopy, boxes, columns, rowContainers, isModalOpen]
  );

  const updateBoxPosition = useCallback(debounce(event => {
    if (selectedBox?.id && boxIsMovable && !selectedBox?.lockedPosition && eventManagerEnabled && !isModalOpen) {
      const move = { h: 1, w: 1 };
      switch (event.key) {
        case 'ArrowUp':
          moveBoxInfinite(
            selectedBox?.left,
            selectedBox?.top - move.h,
            selectedBox?.id
          );
          break;
        case 'ArrowDown':
          moveBoxInfinite(
            selectedBox?.left,
            selectedBox?.top + move.h,
            selectedBox?.id
          );
          break;
        case 'ArrowLeft': {
          moveBoxInfinite(
            selectedBox?.left - move.w,
            selectedBox?.top,
            selectedBox?.id
          );
          break;
        }
        case 'ArrowRight': {
          moveBoxInfinite(
            selectedBox?.left + move.w,
            selectedBox?.top,
            selectedBox?.id
          );
          break;
        }
        default:
          break;
      }
    }
  }, 1), [
    selectedBox?.id,
    selectedBox?.left,
    selectedBox?.top,
    boxIsMovable,
    eventManagerEnabled,
    moveBoxInfinite,
    isModalOpen
  ]);

  const handleArrowKeys = useCallback(
    (event) => {
      const isInTextInput = event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable;

      if (eventManagerEnabled && ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes(event.key) && !isModalOpen && !isInTextInput) {
        event.preventDefault();
        updateBoxPosition(event);
      }
    },
    [
      updateBoxPosition,
      eventManagerEnabled,
      isModalOpen
    ]
  );

  const handleRemoveKeys = useCallback(
    (event) => {
      const isInTextInput = event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable;

      const column = columns.find(col => col.id === selectedBox?.columnId);
      const isInDrawMode = column?.drawMode;
      const isSpecialBox = selectedBox?.type === 'img' || selectedBox?.type === 'shape' || selectedBox?.type === 'line';

      if (
        selectedBox?.id &&
        eventManagerEnabled &&
        !isModalOpen &&
        !isInTextInput &&
        (isInDrawMode || isSpecialBox)
      ) {
        switch (event.key) {
          case 'Delete': {
            event.preventDefault();
            setEventManagerEnabled(false);
            modal.confirm({
              title: "Supprimer l'élément sélectionné ?",
              okText: 'Oui',
              cancelText: 'Non',
              onCancel: () => {
                setEventManagerEnabled(true);
              },
              onOk: () => {
                removeBox(selectedBox?.id);
                setEventManagerEnabled(true);
              },
            });
            break;
          }
          case 'Backspace': {
            event.preventDefault();
            setEventManagerEnabled(false);
            modal.confirm({
              title: "Supprimer l'élément sélectionné ?",
              okText: 'Oui',
              cancelText: 'Non',
              onCancel: () => {
                setEventManagerEnabled(true);
              },
              onOk: () => {
                removeBox(selectedBox?.id);
                setEventManagerEnabled(true);
              },
            });
            break;
          }
          default:
            break;
        }
      }

      const isDeletePressed =
        event.key === 'Delete' ||
        event.key === 'Del' ||
        event.key === 'Suppr' ||
        event.keyCode === 46 ||
        event.which === 46;
      const isBackspacePressed = event.key === 'Backspace';
      const canDeleteContainers =
        !selectedBox?.id &&
        selectedContainerIds?.length > 0 &&
        (currentDocument.type === 'template' || configuration.unlockProposal);

      if ((isDeletePressed || isBackspacePressed) && canDeleteContainers && !isInTextInput && eventManagerEnabled && !isModalOpen) {
        event.preventDefault();
        // Réactivé par l'observateur des modales à la fermeture de la boîte.
        setEventManagerEnabled(false);
        deleteSelectedContainers();
      }
    },
    [
      selectedBox?.id,
      selectedBox?.columnId,
      selectedBox?.type,
      selectedContainerIds,
      eventManagerEnabled,
      setEventManagerEnabled,
      removeBox,
      deleteSelectedContainers,
      currentDocument.type,
      configuration.unlockProposal,
      isModalOpen,
      columns,
      modal,
    ]
  );

  const handleSearchUrlChange = useCallback(
    (event) => {
      if (!fromPdf) { setCurrentDocument(getUrlParamsObject()); }
    },
    [setCurrentDocument, fromPdf]
  );

  const resetSlider = useCallback(
    (e) => {
      if (slider?.from?.value) {
        setSlider(defaultSlider);
      }
    },
    [setSlider, slider.from.value]
  );

  useEffect(() => {
    if (selectedBox && !boxVariableSelectedId) {
      window.addEventListener('keydown', handleArrowKeys);
      window.addEventListener('keydown', handleCtrlCopy);
      window.addEventListener('keydown', handleRemoveKeys);
      return () => {
        window.removeEventListener('keydown', handleArrowKeys);
        window.removeEventListener('keydown', handleCtrlCopy);
        window.removeEventListener('keydown', handleRemoveKeys);
      };
    }
    if ((selectedContainerIds?.length > 0 || selectedContainer) && !selectedBox) {
      window.addEventListener('keydown', handleCtrlCopyContainer);
      window.addEventListener('keydown', handleRemoveKeys);
      return () => {
        window.removeEventListener('keydown', handleCtrlCopyContainer);
        window.removeEventListener('keydown', handleRemoveKeys);
      };
    }
  }, [
    selectedBox?.id,
    handleCtrlCopyContainer,
    handleRemoveKeys,
    selectedBox,
    selectedContainer,
    selectedContainerIds,
    resetSlider,
    boxVariableSelectedId
  ]);

  useEffect(() => {
    window.addEventListener(searchUrlChangedEvent, handleSearchUrlChange);
    return () => {
      window.removeEventListener(searchUrlChangedEvent, handleSearchUrlChange);
    };
  }, [handleSearchUrlChange]);

  return <div onMouseUp={resetSlider}>{children}</div>;
};

export default EventManager;
